// Material Requests, Material Inward, the Tank Register and Suppliers —
// the inventory day work (client, 2026-10-24). Server: src/api-inventory/.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

export type MaterialCategory = 'raw_material' | 'consumables' | 'packaging_material' | 'return';
export type RequestStatus = 'pending_approval' | 'approved' | 'partially_issued' | 'issued' | 'rejected' | 'cancelled';
export type TankStatus = 'full' | 'empty' | 'running' | 'partial' | 'leakage' | 'other';

export interface MaterialItem {
  _id: string;
  name: string;
  sku: string;
  unit: string;
  packSize: string;
  itemType: string;
  onHand: number;
}

export interface Supplier {
  _id: string;
  name: string;
  aliases: string[];
  phone: string;
  gstNumber: string;
  city: string;
  isActive: boolean;
}

export interface RequestLine {
  _id: string;
  productId: string | null;
  itemName: string;
  category: 'raw_material' | 'packaging_material' | 'consumables';
  quantity: number;
  unit: string;
  approvedQty: number | null;
  issuedQty: number;
  issuedLots?: { lotNumber: string; quantity: number }[];
  source: 'warehouse' | 'tank';
  tankCode: string;
  note: string;
  onHand: number | null;
}

export interface MaterialRequest {
  _id: string;
  requestNumber: string;
  purpose: string;
  reference: string;
  requiredBy: string | null;
  priority: 'normal' | 'high' | 'urgent';
  notes: string;
  lines: RequestLine[];
  status: RequestStatus;
  requestedBy: string;
  requestedByName: string;
  requestedByDept: string;
  decidedByName: string;
  decidedAt: string | null;
  decisionNote: string;
  issuedByName: string;
  issuedAt: string | null;
  createdAt: string;
  history: { at: string; byName: string; action: string; note: string }[];
}

export interface RequestsResponse {
  requests: MaterialRequest[];
  counts: Partial<Record<RequestStatus, number>>;
  canApprove: boolean;
  me: string;
  purposes: Record<string, string>;
  categories: Record<string, string>;
  statuses: Record<RequestStatus, string>;
}

export interface MaterialInward {
  _id: string;
  inwardNumber: string;
  dateKey: string;
  category: MaterialCategory;
  supplierId: string | null;
  supplierName: string;
  productId: string | null;
  itemName: string;
  packSize: number;
  packUnit: string;
  packCount: number;
  totalQty: number;
  invoiceNumber: string;
  transportMode: 'self' | 'supplier' | 'transporter' | 'courier';
  transporterName: string;
  vehicleNumber: string;
  destination: 'warehouse' | 'tank';
  tankCode: string;
  rate: number;
  batchNumber: string;
  expiryDate: string | null;
  density: number;
  quantityLitres: number | null;
  quantityKg: number | null;
  qcStatus: 'not_required' | 'pending' | 'accepted' | 'partly_accepted' | 'rejected';
  qc?: {
    acceptedQty: number | null; rejectedQty: number | null; density: number | null; viscosity40: number | null;
    appearance: string; waterPpm: number | null; flashPointC: number | null; remarks: string;
    coaUrl: string; coaName: string; checkedByName: string; checkedAt: string | null;
  };
  returnAgainst: string;
  remarks: string;
  stockQuantity: number;
  stockBasis: 'packs' | 'total';
  stockPosted: boolean;
  status: 'received' | 'cancelled';
  cancelReason: string;
  receivedByName: string;
  createdAt: string;
}

export interface InwardResponse {
  inwards: MaterialInward[];
  summary: { count: number; byCategory: Record<MaterialCategory, number>; notInStock: number; qcPending: number; suppliers: number };
  categories: Record<MaterialCategory, string>;
  transportModes: Record<string, string>;
  qcStatuses: Record<MaterialInward['qcStatus'], string>;
  canQc: boolean;
}

export interface Tank {
  _id: string;
  code: string;
  name: string;
  group: 'yellow' | 'green' | 'white' | 'amber' | 'orange' | 'red';
  groupLabel: string;
  capacityKl: number | null;
  location: string;
  isActive: boolean;
  sortOrder: number;
}

export interface TankEntry {
  dateKey: string;
  tankId: string;
  tankCode: string;
  raw: string;
  product: string;
  status: TankStatus;
  quantityKl: number | null;
  enteredByName: string;
  updatedAt: string;
  history: { raw: string; at: string; byName: string }[];
}

export interface TankRegisterResponse {
  from: string;
  to: string;
  tanks: Tank[];
  entries: TankEntry[];
  latest: TankEntry[];
  statuses: Record<TankStatus, string>;
  groups: Record<string, string>;
}

const errorText = (err: unknown, fallback: string) => {
  const e = err as { response?: { data?: { error?: string } } };
  return e?.response?.data?.error || fallback;
};
export { errorText as materialErrorText };

// ── Items & suppliers ────────────────────────────────────────────────────────

export function useMaterialItemSearch(q: string) {
  return useQuery({
    queryKey: ['inventory', 'material-items', q],
    enabled: q.trim().length >= 2,
    staleTime: 30_000,
    queryFn: async () => (await api.get<{ items: MaterialItem[] }>('/api/inventory/material-requests/items', { params: { q } })).data.items,
  });
}

export function useSuppliers(all = false) {
  return useQuery({
    queryKey: ['inventory', 'suppliers', all],
    staleTime: 60_000,
    queryFn: async () => (await api.get<{ suppliers: Supplier[] }>('/api/inventory/suppliers', { params: all ? { all: 'true' } : {} })).data.suppliers,
  });
}

export function useSaveSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: Partial<Supplier> & { id?: string }) => (
      id ? (await api.put<{ supplier: Supplier }>(`/api/inventory/suppliers/${id}`, body)).data.supplier
        : (await api.post<{ supplier: Supplier }>('/api/inventory/suppliers', body)).data.supplier
    ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inventory', 'suppliers'] }),
  });
}

// ── Material requests ────────────────────────────────────────────────────────

export function useMaterialRequests(status: string, q: string, mine: boolean, enabled = true) {
  return useQuery({
    queryKey: ['inventory', 'material-requests', status, q, mine],
    enabled,
    refetchInterval: 60_000,
    queryFn: async () => (await api.get<RequestsResponse>('/api/inventory/material-requests', {
      params: { ...(status !== 'all' ? { status } : {}), ...(q ? { q } : {}), ...(mine ? { mine: 'true' } : {}) },
    })).data,
  });
}

export function useMaterialRequestAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, action, body }: { id?: string; action: 'create' | 'approve' | 'reject' | 'cancel' | 'issue'; body?: unknown }) => {
      const url = action === 'create' ? '/api/inventory/material-requests' : `/api/inventory/material-requests/${id}/${action}`;
      return (await api.post<{ request: MaterialRequest; warnings?: string[]; itemsAdded?: string[] }>(url, body ?? {})).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory', 'material-requests'] });
      qc.invalidateQueries({ queryKey: ['inventory', 'balances'] });
      qc.invalidateQueries({ queryKey: ['inventory', 'tank-levels'] });
    },
  });
}

// ── Material inward ──────────────────────────────────────────────────────────

export function useMaterialInwards(filters: { from: string; to: string; category: string; q: string }) {
  return useQuery({
    queryKey: ['inventory', 'material-inward', filters],
    queryFn: async () => (await api.get<InwardResponse>('/api/inventory/material-inward', {
      params: {
        from: filters.from || undefined, to: filters.to || undefined,
        category: filters.category !== 'all' ? filters.category : undefined, q: filters.q || undefined,
      },
    })).data,
  });
}

export function useMaterialInwardAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, action, body }: { id?: string; action: 'create' | 'cancel' | 'link-item'; body?: unknown }) => {
      const url = action === 'create' ? '/api/inventory/material-inward' : `/api/inventory/material-inward/${id}/${action}`;
      return (await api.post<{ inward: MaterialInward; stockWarning?: string; itemAdded?: boolean }>(url, body ?? {})).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory', 'material-inward'] });
      qc.invalidateQueries({ queryKey: ['inventory', 'balances'] });
      qc.invalidateQueries({ queryKey: ['inventory', 'tank-levels'] });
    },
  });
}

// ── Tank register ────────────────────────────────────────────────────────────

export function useTankRegister(from: string, to: string) {
  return useQuery({
    queryKey: ['inventory', 'tank-register', from, to],
    queryFn: async () => (await api.get<TankRegisterResponse>('/api/inventory/tanks/register', { params: { from, to } })).data,
  });
}

export function useSaveTankDay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ dateKey, cells }: { dateKey: string; cells: { tankId: string; text: string }[] }) => (
      (await api.put<{ created: number; updated: number; cleared: number; unchanged: number }>(`/api/inventory/tanks/register/${dateKey}`, { cells })).data
    ),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: ['inventory', 'tank-register'] }), qc.invalidateQueries({ queryKey: ['inventory', 'tank-levels'] })]),
  });
}

export function useSaveTank() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: Partial<Tank> & { id?: string }) => (
      id ? (await api.put<{ tank: Tank }>(`/api/inventory/tanks/${id}`, body)).data.tank
        : (await api.post<{ tank: Tank }>('/api/inventory/tanks', body)).data.tank
    ),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: ['inventory', 'tank-register'] }), qc.invalidateQueries({ queryKey: ['inventory', 'tank-levels'] })]),
  });
}

export async function importTankSheet(file: File, dryRun: boolean) {
  const form = new FormData();
  form.append('file', file);
  const { data } = await api.post<{
    days: number; cells: number; from: string | null; to: string | null; skippedRows: number; unknownTanks: string[];
    created?: number; updated?: number; unchanged?: number;
  }>(`/api/inventory/tanks/register/import${dryRun ? '?dryRun=true' : ''}`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  return data;
}

/** Download rows as a CSV file the user opens in Excel. */
export function downloadCsv(filename: string, header: string[], rows: (string | number | null | undefined)[][]) {
  const esc = (v: string | number | null | undefined) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [header, ...rows].map((r) => r.map(esc).join(',')).join('\r\n');
  // A byte-order mark first, so Excel opens the file as UTF-8 (₹, Hindi names).
  const blob = new Blob([String.fromCharCode(0xfeff) + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}
